// Menu Guideline / Flow Maps — single source of truth
// Readiness states (locked iter 1, ticket #27):
//   ready    – module live, click navigates to route
//   partial  – backend live but UI subset or sub-flow incomplete
//   beta     – usable but signed off as Beta (yellow border)
//   disabled – "Mờ" sắp ra mắt
//
// Schema (per tab):
//   key, label, icon (lucide name), groupBadge?
//   readiness: "ready" | "partial" | "beta" | "disabled"
//   phases: [{ id, no, title, subtitle?, route?, readiness?, badge?, subItems?, optional? }]
//   arrows: [{ from: phaseId, to: phaseId, kind: "primary" | "optional" }]
//   metrics: [{ key, label, suffix?, endpoint?, source: "api"|"derived"|"static", staticValue? }]
//   postsToLedger?: [{ account, dr?, cr? }]
//   hasGuide: boolean — toggle "Sắp ra mắt" badge when false
//   guide?: { steps: [{ no, title, body, screenshot?, account? }] }
//   noGuideTooltip?: string (override "Hướng dẫn sắp ra mắt")
//
// Validation: any tab missing required keys logs a console.warn at startup
// (see validateMenuGuidelineConfig below).

export const READINESS_META = {
  ready:    { label: "Đã sẵn sàng", color: "emerald",
              ring: "ring-emerald-700",   pill: "bg-emerald-100 text-emerald-900" },
  partial:  { label: "Một phần",   color: "amber",
              ring: "ring-amber-500",     pill: "bg-amber-100 text-amber-900" },
  beta:     { label: "Beta",       color: "yellow",
              ring: "ring-yellow-500",    pill: "bg-yellow-100 text-yellow-900" },
  disabled: { label: "Sắp ra mắt", color: "stone",
              ring: "ring-stone-300",     pill: "bg-stone-200 text-stone-600" },
};

export const MENU_GUIDELINE_CONFIG = [
  // ─────────────────────────── GL Hub ───────────────────────────
  {
    key: "gl-hub",
    label: "GL Hub",
    icon: "Activity",
    readiness: "disabled",
    hasGuide: false,
    layout: "hub",
    centerNode: { id: "gl", title: "General Ledger", subtitle: "Sổ cái trung tâm" },
    sources: [
      { id: "src-inv",  title: "Invoice",          tone: "emerald" },
      { id: "src-bill", title: "Bill",             tone: "amber" },
      { id: "src-exp",  title: "Expense",          tone: "rose" },
      { id: "src-pr",   title: "Payment received", tone: "emerald" },
      { id: "src-pm",   title: "Payment made",     tone: "amber" },
      { id: "src-bank", title: "Bank transaction", tone: "blue" },
      { id: "src-stk",  title: "Inventory adj.",   tone: "violet" },
      { id: "src-mj",   title: "Manual journal",   tone: "stone" },
    ],
    reports: [
      { id: "r-pnl",   title: "Profit & Loss" },
      { id: "r-bs",    title: "Balance Sheet" },
      { id: "r-cf",    title: "Cash Flow" },
      { id: "r-tb",    title: "Trial Balance" },
      { id: "r-ar",    title: "A/R Aging" },
      { id: "r-ap",    title: "A/P Aging" },
      { id: "r-vat",   title: "VAT Summary" },
      { id: "r-gl",    title: "General Ledger" },
    ],
    metrics: [
      { key: "posted_mtd",  label: "POSTED (MTD)",  source: "static", staticValue: "—" },
      { key: "pending",     label: "PENDING",       source: "static", staticValue: "—" },
      { key: "books",       label: "BOOKS STATUS",  source: "static", staticValue: "Đang chuẩn bị" },
      { key: "net_vat",     label: "NET VAT",       source: "static", staticValue: "—" },
    ],
  },

  // ─────────────────────────── Sales ───────────────────────────
  {
    key: "sales",
    label: "Sales",
    icon: "ShoppingCart",
    readiness: "partial",
    hasGuide: false,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Quote",   subtitle: "Báo giá cho khách",          readiness: "disabled" },
      { id: "p2", no: "02", title: "Issue",   subtitle: "Phát hành hoá đơn / phiếu",  readiness: "ready",   route: "/admin/pos",
        subItems: [
          { label: "Invoice",  state: "ready" },
          { label: "Estimate", state: "disabled", optional: true },
        ] },
      { id: "p3", no: "03", title: "Collect", subtitle: "Thu tiền POS / chuyển khoản", readiness: "ready",  route: "/admin/pos" },
      { id: "p4", no: "04", title: "Deposit", subtitle: "Đối soát ngân hàng",         readiness: "disabled" },
      { id: "p5", no: "05", title: "Book",    subtitle: "Ghi sổ kế toán",             readiness: "disabled" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
      { from: "p4", to: "p5", kind: "primary" },
    ],
    metrics: [
      { key: "open_ar",    label: "OPEN A/R",       source: "static", staticValue: "—" },
      { key: "overdue",    label: "OVERDUE",        source: "static", staticValue: "—" },
      { key: "avg_collect",label: "AVG. COLLECTION",source: "static", staticValue: "—" },
      { key: "conversion", label: "EST. CONVERSION",source: "static", staticValue: "—" },
    ],
    postsToLedger: [
      { account: "1311 Phải thu KH", dr: true },
      { account: "5111 Doanh thu",   cr: true },
      { account: "33311 VAT đầu ra", cr: true },
    ],
  },

  // ─────────────────────────── Purchases ───────────────────────────
  {
    key: "purchases",
    label: "Purchases",
    icon: "ShoppingBag",
    readiness: "partial",
    hasGuide: false,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "RFQ", subtitle: "Yêu cầu báo giá",     readiness: "disabled" },
      { id: "p2", no: "02", title: "PO",  subtitle: "Đơn đặt hàng",        readiness: "disabled" },
      { id: "p3", no: "03", title: "GRN", subtitle: "Nhập kho",            readiness: "partial", route: "/admin/stock" },
      { id: "p4", no: "04", title: "Bill",subtitle: "Hoá đơn nhà cung cấp",readiness: "partial", route: "/admin/ocr" },
      { id: "p5", no: "05", title: "Pay", subtitle: "Thanh toán NCC",      readiness: "disabled" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
      { from: "p4", to: "p5", kind: "primary" },
    ],
    metrics: [
      { key: "open_ap",   label: "OPEN A/P",    source: "static", staticValue: "—" },
      { key: "po_pending",label: "PO PENDING",  source: "static", staticValue: "—" },
      { key: "grn_today", label: "GRN HÔM NAY", source: "static", staticValue: "—" },
      { key: "bills_ocr", label: "BILLS OCR",   source: "static", staticValue: "—" },
    ],
  },

  // ─────────────────────────── Inventory ───────────────────────────
  {
    key: "inventory",
    label: "Inventory",
    icon: "Warehouse",
    readiness: "ready",
    hasGuide: true,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Items",       subtitle: "Danh mục mặt hàng", readiness: "ready", route: "/admin/master" },
      { id: "p2", no: "02", title: "Warehouses",  subtitle: "Kho & vị trí",       readiness: "ready", route: "/admin/stock" },
      { id: "p3", no: "03", title: "Movements",   subtitle: "Nhập/Xuất/Chuyển",   readiness: "ready", route: "/admin/stock" },
      { id: "p4", no: "04", title: "Adjustments", subtitle: "Kiểm kê & điều chỉnh",readiness: "ready", route: "/admin/stock" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
    ],
    metrics: [
      { key: "items",      label: "MÃ HÀNG",      source: "api", endpoint: "/master/items/count" },
      { key: "warehouses", label: "KHO HOẠT ĐỘNG",source: "static", staticValue: "—" },
      { key: "low_stock",  label: "DƯỚI MIN",     source: "static", staticValue: "—" },
      { key: "moves_today",label: "PHIẾU / NGÀY", source: "static", staticValue: "—" },
    ],
    guide: {
      steps: [
        { no: 1, title: "Đăng nhập",
          body: "Đăng nhập với tài khoản admin@thanhson.vn để truy cập đầy đủ menu Kho." },
        { no: 2, title: "Khởi tạo Items",
          body: "Mở Master Data → tab Items, thêm mã hàng mới với SKU, đơn vị tính và giá vốn." },
        { no: 3, title: "Thiết lập Warehouses",
          body: "Mở Kho tổng → tab Warehouses, khai báo từng kho theo pháp nhân (8 entities)." },
        { no: 4, title: "Ghi nhận Movements",
          body: "Tạo phiếu Nhập / Xuất / Chuyển kho. Mỗi phiếu post 1 movement, kiểm tra số tồn ngay sau khi lưu." },
        { no: 5, title: "Kiểm kê & Điều chỉnh",
          body: "Định kỳ chạy kiểm kê. Phát sinh chênh lệch ghi nhận adjustment, hệ thống tự cập nhật giá vốn bình quân gia quyền." },
      ],
    },
  },

  // ─────────────────────────── Banking ───────────────────────────
  {
    key: "banking",
    label: "Banking",
    icon: "Landmark",
    readiness: "disabled",
    hasGuide: false,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Feed",    subtitle: "Đồng bộ sao kê",       readiness: "disabled" },
      { id: "p2", no: "02", title: "Review",  subtitle: "Soát giao dịch",       readiness: "disabled" },
      { id: "p3", no: "03", title: "Decide",  subtitle: "Match / Categorize / Auto-rule / Exclude", readiness: "disabled",
        subItems: [
          { label: "Match",         state: "disabled" },
          { label: "Categorize",    state: "disabled" },
          { label: "Auto-rule",     state: "disabled", optional: true },
          { label: "Exclude",       state: "disabled", optional: true },
        ] },
      { id: "p4", no: "04", title: "Close",   subtitle: "Đối soát chốt kỳ",     readiness: "disabled" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
    ],
    metrics: [
      { key: "connected",  label: "CONNECTED",        source: "static", staticValue: "0" },
      { key: "to_review",  label: "TO REVIEW",        source: "static", staticValue: "—" },
      { key: "auto_pct",   label: "AUTO-CATEGORIZED", source: "static", staticValue: "—", suffix: "%" },
      { key: "rules",      label: "BANK RULES",       source: "static", staticValue: "0" },
    ],
  },

  // ─────────────────────────── Month-end ───────────────────────────
  {
    key: "month-end",
    label: "Month-end",
    icon: "CalendarRange",
    readiness: "partial",
    hasGuide: false,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Reconcile", subtitle: "Đối chiếu sổ phụ",   readiness: "disabled" },
      { id: "p2", no: "02", title: "Adjust",    subtitle: "Bút toán điều chỉnh",readiness: "disabled" },
      { id: "p3", no: "03", title: "Close",     subtitle: "Khoá sổ kỳ",         readiness: "disabled" },
      { id: "p4", no: "04", title: "Report",    subtitle: "Xuất báo cáo",       readiness: "partial", route: "/admin/audit-history" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
    ],
    metrics: [
      { key: "period",     label: "KỲ HIỆN TẠI",   source: "static", staticValue: "—" },
      { key: "open_journals", label: "JE MỞ",      source: "static", staticValue: "—" },
      { key: "reconciled", label: "ĐÃ ĐỐI CHIẾU",  source: "static", staticValue: "—" },
      { key: "books_status", label: "BOOKS",       source: "static", staticValue: "Đang chuẩn bị" },
    ],
  },

  // ─────────────────────────── HRM ───────────────────────────
  {
    key: "hrm",
    label: "HRM",
    icon: "Users",
    readiness: "ready",
    hasGuide: true,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Positions",   subtitle: "Vị trí & bậc công việc", readiness: "ready", route: "/admin/hrm-positions" },
      { id: "p2", no: "02", title: "Transfers",   subtitle: "Điều chuyển vị trí",     readiness: "ready", route: "/admin/employees" },
      { id: "p3", no: "03", title: "Daily-tasks", subtitle: "Công việc hằng ngày",    readiness: "ready", route: "/admin/hrm-daily-work" },
      { id: "p4", no: "04", title: "Training",    subtitle: "Đào tạo Sư phạm",        readiness: "ready", route: "/admin/hrm-training" },
      { id: "p5", no: "05", title: "Incidents",   subtitle: "Sự cố & xử lý",          readiness: "ready", route: "/admin/hrm-incidents" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
      { from: "p4", to: "p5", kind: "primary" },
    ],
    metrics: [
      { key: "active_emp",  label: "NHÂN SỰ ACTIVE", source: "api", endpoint: "/dashboard/overview", apiPath: "stats.employees" },
      { key: "positions",   label: "VỊ TRÍ",         source: "static", staticValue: "—" },
      { key: "training",    label: "ĐÀO TẠO MỞ",     source: "static", staticValue: "—" },
      { key: "incidents",   label: "SỰ CỐ TUẦN",     source: "static", staticValue: "—" },
    ],
    guide: {
      steps: [
        { no: 1, title: "Khởi tạo Positions",
          body: "Mở HR-Ops → HRM Positions, định nghĩa vị trí + bậc công việc cho từng pháp nhân." },
        { no: 2, title: "Điều chuyển vị trí",
          body: "Tại Nhân sự, mở hồ sơ → tab Transfer history, ghi nhận điều chuyển có hiệu lực từ ngày X." },
        { no: 3, title: "Sinh công việc hằng ngày",
          body: "HRM Daily Work → chọn cơ sở & ngày → Generate. Hệ thống dùng pg advisory lock tránh trùng sinh." },
        { no: 4, title: "Lên kế hoạch đào tạo",
          body: "HRM Training → tạo đợt đào tạo Sư phạm, gắn người tham dự và lịch." },
        { no: 5, title: "Ghi nhận sự cố",
          body: "HRM Incidents → tạo phiếu sự cố. Mỗi mutation ghi audit_events đầy đủ actor/action." },
      ],
    },
  },

  // ─────────────────────────── Legal Vault ───────────────────────────
  {
    key: "legal-vault",
    label: "Legal Vault",
    icon: "FolderLock",
    readiness: "ready",
    hasGuide: true,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Entities",   subtitle: "Pháp nhân & phạm vi",   readiness: "ready", route: "/admin/companies" },
      { id: "p2", no: "02", title: "Doc types",  subtitle: "Loại tài liệu",         readiness: "ready", route: "/admin/legal-vault" },
      { id: "p3", no: "03", title: "Documents",  subtitle: "Kho tài liệu",          readiness: "ready", route: "/admin/legal-vault" },
      { id: "p4", no: "04", title: "Requests",   subtitle: "Yêu cầu truy cập",      readiness: "ready", route: "/admin/legal-requests" },
      { id: "p5", no: "05", title: "Approve",    subtitle: "Phê duyệt",             readiness: "ready", route: "/admin/legal-requests" },
      { id: "p6", no: "06", title: "Grants",     subtitle: "Cấp quyền có thời hạn", readiness: "ready", route: "/admin/legal-grants" },
      { id: "p7", no: "07", title: "Audit",      subtitle: "Nhật ký pháp lý",       readiness: "ready", route: "/admin/legal-audit" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
      { from: "p4", to: "p5", kind: "primary" },
      { from: "p5", to: "p6", kind: "primary" },
      { from: "p6", to: "p7", kind: "primary" },
    ],
    metrics: [
      { key: "entities",  label: "PHÁP NHÂN",   source: "api", endpoint: "/dashboard/overview", apiPath: "stats.companies" },
      { key: "docs",      label: "TÀI LIỆU",    source: "static", staticValue: "—" },
      { key: "requests",  label: "REQ MỞ",      source: "static", staticValue: "—" },
      { key: "grants",    label: "GRANT ACTIVE",source: "static", staticValue: "—" },
    ],
    guide: {
      steps: [
        { no: 1, title: "Khai báo pháp nhân",
          body: "Tổ chức → Pháp nhân & Phòng ban, đảm bảo 8 entities Thanh Sơn đã active." },
        { no: 2, title: "Định nghĩa Doc types",
          body: "Kho tài liệu → tab Doc types, khai báo loại (Giấy phép, HĐ, BHXH…) kèm chu kỳ nhắc gia hạn." },
        { no: 3, title: "Upload tài liệu",
          body: "Kho tài liệu → Upload. File lưu qua legal_storage; checksum + version tự sinh." },
        { no: 4, title: "Tạo Request",
          body: "User (legal_requester) submit Request gắn doc + scope (read/download)." },
        { no: 5, title: "Phê duyệt",
          body: "Approver mở Phê duyệt yêu cầu, chấp nhận / từ chối; mọi action ghi audit_events." },
        { no: 6, title: "Quản lý Grant",
          body: "Cấp quyền có TTL. Hệ thống auto-revoke khi expired (xem legal_scheduler)." },
        { no: 7, title: "Audit",
          body: "Mở Nhật ký pháp lý để truy vết toàn bộ thao tác theo actor/target/time." },
      ],
    },
  },

  // ─────────────────────────── F&B Operations ───────────────────────────
  {
    key: "fnb",
    label: "F&B Operations",
    icon: "ChefHat",
    readiness: "ready",
    hasGuide: true,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "POS",          subtitle: "Bán hàng đa kênh",      readiness: "ready", route: "/admin/pos" },
      { id: "p2", no: "02", title: "KDS",          subtitle: "Kitchen Display",       readiness: "ready", route: "/staff/kds" },
      { id: "p3", no: "03", title: "Kitchen",      subtitle: "Bếp tổng BOM/MRP",      readiness: "beta",  route: "/admin/kitchen", badge: "Beta" },
      { id: "p4", no: "04", title: "HTX",          subtitle: "Nông sản HTX",          readiness: "ready", route: "/admin/htx" },
      { id: "p5", no: "05", title: "Multi-cơ sở",  subtitle: "Vận hành chuỗi",        readiness: "partial" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "optional" },
      { from: "p3", to: "p5", kind: "primary" },
    ],
    metrics: [
      { key: "pos_today",  label: "ĐƠN HÔM NAY", source: "static", staticValue: "—" },
      { key: "kds_open",   label: "KDS MỞ",      source: "static", staticValue: "—" },
      { key: "bom_active", label: "BOM ACTIVE",  source: "static", staticValue: "—" },
      { key: "co_so",      label: "CƠ SỞ",       source: "static", staticValue: "—" },
    ],
    guide: {
      steps: [
        { no: 1, title: "Chọn cơ sở & kênh bán",
          body: "Mở POS đa kênh → chọn chi nhánh, rồi chọn tab kênh: FMCG Retail, Nhà hàng, hoặc Đại lý B2B. Căn tin & nhà hàng dùng chung kênh \"Nhà hàng\"." },
        { no: 2, title: "Mở bàn & gọi món",
          body: "Kênh Nhà hàng: nhập số bàn, chọn món từ lưới SKU để thêm vào giỏ. Có thể lưu nháp và mở lại đơn dở khi cần." },
        { no: 3, title: "Đẩy bếp qua KDS",
          body: "Đơn lên màn hình bếp KDS (/staff/kds). Bếp mở/đóng ca, cập nhật trạng thái từng món để phục vụ kịp." },
        { no: 4, title: "Thanh toán",
          body: "Mở Thanh toán → chọn VAT (0/5/8/10%) và phương thức (tiền mặt, thẻ, chuyển khoản, công nợ). Hỗ trợ chia nhiều phương thức trên một hóa đơn." },
        { no: 5, title: "Theo dõi đơn gần đây",
          body: "Khung \"Đơn gần đây\" lọc theo trạng thái (Mở / Đã thu / Đã hủy / Đã hoàn) và chi nhánh để soát doanh thu trong ca." },
        { no: 6, title: "Định mức nguyên liệu (Bếp tổng)",
          body: "Bếp tổng BOM/MRP (beta): khai định mức nguyên liệu theo món, hệ thống tính nhu cầu để xuất kho / đặt mua." },
        { no: 7, title: "Nguồn cung & vận hành chuỗi",
          body: "HTX cung cấp nông sản đầu vào cho bếp. Multi-cơ sở quản lý nhiều nhà hàng/căn tin (đang hoàn thiện)." },
      ],
    },
  },

  // ─────────────────────────── Multi-entity ───────────────────────────
  {
    key: "multi-entity",
    label: "Pháp nhân",
    icon: "Building2",
    readiness: "partial",
    hasGuide: false,
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Entities",   subtitle: "8 pháp nhân Thanh Sơn",  readiness: "ready",   route: "/admin/companies" },
      { id: "p2", no: "02", title: "Cross-tenant", subtitle: "Khung nhìn liên pháp nhân", readiness: "partial" },
      { id: "p3", no: "03", title: "Consolidation", subtitle: "Hợp nhất báo cáo",     readiness: "disabled" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
    ],
    metrics: [
      { key: "entities", label: "PHÁP NHÂN", source: "api", endpoint: "/dashboard/overview", apiPath: "stats.companies" },
      { key: "branches", label: "CHI NHÁNH", source: "api", endpoint: "/dashboard/overview", apiPath: "stats.branches" },
      { key: "consol",   label: "CONSOL",    source: "static", staticValue: "—" },
      { key: "books",    label: "BOOKS",     source: "static", staticValue: "—" },
    ],
  },

  // ─────────────────────────── Excel Workspace ───────────────────────────
  {
    key: "excel",
    label: "Excel Workspace",
    icon: "FileSpreadsheet",
    readiness: "ready",
    hasGuide: false,
    noGuideTooltip: "Hướng dẫn Excel Workspace ra mắt ở issue follow-up #27.x",
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Import", subtitle: "Tải file mẫu",        readiness: "ready", route: "/admin/excel" },
      { id: "p2", no: "02", title: "Map",    subtitle: "Khớp cột",            readiness: "ready", route: "/admin/excel" },
      { id: "p3", no: "03", title: "Validate", subtitle: "Kiểm tra dữ liệu",  readiness: "ready", route: "/admin/excel" },
      { id: "p4", no: "04", title: "Apply",  subtitle: "Ghi nhận vào hệ thống", readiness: "ready", route: "/admin/excel" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
    ],
    metrics: [
      { key: "templates", label: "BIỂU MẪU",  source: "static", staticValue: "—" },
      { key: "imports",   label: "IMPORT TUẦN", source: "static", staticValue: "—" },
      { key: "exports",   label: "EXPORT TUẦN", source: "static", staticValue: "—" },
      { key: "errors",    label: "LỖI VALIDATE",source: "static", staticValue: "—" },
    ],
  },

  // ─────────────────────────── OCR Invoice ───────────────────────────
  {
    key: "ocr",
    label: "OCR Invoice (AI)",
    icon: "ScanLine",
    readiness: "ready",
    hasGuide: false,
    noGuideTooltip: "Hướng dẫn OCR ra mắt ở issue follow-up #27.x",
    layout: "flow",
    phases: [
      { id: "p1", no: "01", title: "Upload",  subtitle: "Tải chứng từ ảnh / PDF", readiness: "ready", route: "/admin/ocr" },
      { id: "p2", no: "02", title: "OCR",     subtitle: "AI trích xuất",          readiness: "ready", route: "/admin/ocr" },
      { id: "p3", no: "03", title: "Review",  subtitle: "Soát kết quả",           readiness: "ready", route: "/admin/ocr" },
      { id: "p4", no: "04", title: "Post",    subtitle: "Ghi nhận bill",          readiness: "partial" },
    ],
    arrows: [
      { from: "p1", to: "p2", kind: "primary" },
      { from: "p2", to: "p3", kind: "primary" },
      { from: "p3", to: "p4", kind: "primary" },
    ],
    metrics: [
      { key: "queue",      label: "QUEUE",       source: "static", staticValue: "—" },
      { key: "accuracy",   label: "ACCURACY",    source: "static", staticValue: "—", suffix: "%" },
      { key: "to_review",  label: "TO REVIEW",   source: "static", staticValue: "—" },
      { key: "posted_mtd", label: "POSTED MTD",  source: "static", staticValue: "—" },
    ],
  },
];

// AC4.8 — Lightweight schema validator. Runs once on import (dev only):
// missing required fields print console.warn so devs catch config drift early.
const REQUIRED_KEYS = ["key", "label", "icon", "readiness", "layout"];
const VALID_READINESS = ["ready", "partial", "beta", "disabled"];

export function validateMenuGuidelineConfig(cfg = MENU_GUIDELINE_CONFIG) {
  const issues = [];
  const seenKeys = new Set();
  for (const tab of cfg) {
    for (const k of REQUIRED_KEYS) {
      if (tab[k] == null) issues.push(`tab "${tab.key || "?"}" missing "${k}"`);
    }
    if (seenKeys.has(tab.key)) issues.push(`duplicate tab key "${tab.key}"`);
    seenKeys.add(tab.key);
    if (tab.readiness && !VALID_READINESS.includes(tab.readiness)) {
      issues.push(`tab "${tab.key}" invalid readiness "${tab.readiness}"`);
    }
    if (tab.layout === "flow") {
      if (!Array.isArray(tab.phases) || tab.phases.length === 0) {
        issues.push(`tab "${tab.key}" layout=flow but no phases`);
      }
      if (!Array.isArray(tab.arrows)) {
        issues.push(`tab "${tab.key}" layout=flow but no arrows`);
      }
    }
    if (tab.layout === "hub") {
      if (!tab.centerNode) issues.push(`tab "${tab.key}" layout=hub but no centerNode`);
    }
  }
  return issues;
}

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  const issues = validateMenuGuidelineConfig();
  if (issues.length) {
    // eslint-disable-next-line no-console
    console.warn("[menu-guideline-status] config issues:\n  " + issues.join("\n  "));
  }
}
